import { Tabs } from 'antd';
import { useCurrentUser } from 'client/components/auth/AuthModule';
import { NetworkAlertPage } from 'client/components/errors/network-alert/NetworkAlertPage';
import { useTask } from 'client/components/schema/task/hooks/useTask';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { TaskActionLogic } from 'client/logic/TaskActionLogic';
import { useProcedureNotes } from 'client/routes/procedure/single/notes/useProcedureNotes';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { BreadcrumbsContainer } from 'client/ui/breadcrumb/BreadcrumbsContainer';
import { CorePageLayout } from 'client/ui/layout/CorePageLayout';
import { PageContent } from 'client/ui/layout/page/content/PageContent';
import { PageHeading } from 'client/ui/layout/page/heading/PageHeading';
import { PageTitle } from 'client/ui/layout/page/heading/PageTitle';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { NameResponsive } from 'client/ui/tabs/NameToIconResponsive';
import { TabContentWithBadge } from 'client/ui/tabs/TabContentWithBadge';
import { TabsPageHeading } from 'client/ui/tabs/TabsPageHeading';
import * as React from 'react';
import { CSSProperties, useState } from 'react';
import { TbEdit, TbHistory, TbMessage } from 'react-icons/tb';
import { RouteComponentProps } from 'react-router';
import { TabHistory } from './tab/TabHistory';
import { TabNotes } from './tab/TabNotes';
import { TabTask } from './tab/TabTask';
import { TabTaskPortalContainer } from './tab/TabTaskPortal';
const { TabPane } = Tabs;

function getVisibilityStyle(tab: string, key: string) {
  return tab === key
    ? undefined
    : ({ display: 'none', visibility: 'hidden' } as CSSProperties);
}

interface Params {
  id: string;
}

export interface TaskPageProps extends RouteComponentProps<Params> {}

export function TaskPage(props: TaskPageProps) {
  const [visibleTab, setVisibleTab] = useState('task');
  const currentUser = useCurrentUser();

  const taskId = props.match.params.id;
  const { task, loading, error, notFound } = useTask(taskId);

  const {
    notes,
    loading: notesLoading,
    error: notesError
  } = useProcedureNotes(task?.procedimento?.id);

  useBreadcrumbItem({ path: `/task/list`, title: `Elenco Attività` });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: `${task?.name} - ${task?.procedimento?.nomeFaseCorrente}`
  });

  if (loading || notesLoading) {
    return <LoadingPage full />;
  }

  if (notFound) {
    return (
      <AlertPage
        showIcon
        type="error"
        message="Attività non trovata"
        description={`Nessuna attività trovata con l'ID specificato.`}
      />
    );
  }

  if (error) {
    return (
      <NetworkAlertPage
        message={"Si è verificato un errore durante l'accesso all'attività"}
        error={error}
      />
    );
  }

  return (
    <CorePageLayout>
      <>
        <PageHeading
          breadcrumb={<BreadcrumbsContainer />}
          title={<PageTitle procedure={task?.procedimento} name={task?.name} />}
          extra={<TabTaskPortalContainer />}
          footer={
            <TabsPageHeading
              defaultActiveKey={visibleTab ?? 'task'}
              onChange={key => setVisibleTab(key)}
            >
              <TabPane
                key="task"
                tab={<NameResponsive name="Attività" icon={<TbEdit />} />}
              />
              <TabPane
                key="notes"
                tab={
                  <TabContentWithBadge
                    showBadge={notes.length > 0}
                    icon={<TbMessage />}
                    name="Note"
                  />
                }
              />
              <TabPane
                key="history"
                tab={<NameResponsive name="Cronologia" icon={<TbHistory />} />}
              />
            </TabsPageHeading>
          }
        />
        {/* PageContent */}
        <PageContent>
          <div style={getVisibilityStyle(visibleTab, 'task')}>
            <TabTask
              task={task}
              isAssigned={TaskActionLogic.isAssigned(task, currentUser)}
            />
          </div>
          <div style={getVisibilityStyle(visibleTab, 'notes')}>
            <TabNotes notes={notes} error={notesError} />
          </div>
          <div style={getVisibilityStyle(visibleTab, 'history')}>
            <TabHistory procedureId={task?.procedimento?.id} />
          </div>
        </PageContent>
      </>
    </CorePageLayout>
  );
}
